/**
 * Growthly — Verified Partner Reviews (Standalone Section)
 * File: assets/verified-partner-reviews.js
 * Libraries: GSAP 3 + ScrollTrigger (loaded via CDN by the section)
 * Supports Shopify Online Store 2.0 Theme Editor lifecycle & prefers-reduced-motion
 * Fully self-contained: no Lenis dependency, scoped to [data-vrp-section]
 */
(function () {
  'use strict';

  var instances = new WeakMap();

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function initSection(root) {
    if (!root || instances.has(root)) return;

    var reduced = prefersReducedMotion();
    var cleanups = [];
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    if (gsap && ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    var on = function (target, type, fn) {
      target.addEventListener(type, fn);
      cleanups.push(function () {
        target.removeEventListener(type, fn);
      });
    };

    /* ------------------------------------------------------------------
       1. Magnetic Pill Buttons (Fine Pointer Only)
       ------------------------------------------------------------------ */
    if (!reduced && gsap && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var magnetics = root.querySelectorAll('[data-vrp-magnetic]');
      magnetics.forEach(function (wrap) {
        var strength = parseFloat(wrap.getAttribute('data-vrp-magnetic')) || 0.2;
        var xTo = gsap.quickTo(wrap, 'x', { duration: 0.45, ease: 'power3.out' });
        var yTo = gsap.quickTo(wrap, 'y', { duration: 0.45, ease: 'power3.out' });

        on(wrap, 'mousemove', function (e) {
          var rect = wrap.getBoundingClientRect();
          var cx = rect.left + rect.width / 2;
          var cy = rect.top + rect.height / 2;
          xTo((e.clientX - cx) * strength);
          yTo((e.clientY - cy) * strength);
        });

        on(wrap, 'mouseleave', function () {
          xTo(0);
          yTo(0);
        });
      });
    }

    /* ------------------------------------------------------------------
       2. Mobile Expand Toggle for Compact Reviews
       ------------------------------------------------------------------ */
    var reviewsMoreBtn = root.querySelector('[data-vrp-reviews-more-btn]');
    var reviewsSecondary = root.querySelector('[data-vrp-reviews-secondary]');
    if (reviewsMoreBtn && reviewsSecondary) {
      var reviewsMoreLabel = reviewsMoreBtn.querySelector(
        '[data-vrp-reviews-more-label]'
      );
      var reviewsChevron = reviewsMoreBtn.querySelector(
        '[data-vrp-reviews-chevron]'
      );
      var collapsedLabel = reviewsMoreLabel
        ? reviewsMoreLabel.textContent
        : 'Read more verified reviews';

      on(reviewsMoreBtn, 'click', function () {
        var expanded = reviewsSecondary.classList.toggle('is-expanded');
        reviewsMoreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (reviewsMoreLabel) {
          reviewsMoreLabel.textContent = expanded
            ? 'Show fewer reviews'
            : collapsedLabel;
        }
        if (reviewsChevron) {
          reviewsChevron.style.transform = expanded
            ? 'rotate(180deg)'
            : 'rotate(0deg)';
        }
        if (ScrollTrigger) {
          window.requestAnimationFrame(function () {
            ScrollTrigger.refresh();
          });
        }
      });
    }

    /* ------------------------------------------------------------------
       3. Scroll Animations (GSAP + ScrollTrigger)
       ------------------------------------------------------------------ */
    var ctx = null;

    if (gsap && ScrollTrigger && !reduced) {
      ctx = gsap.context(function () {
        /* A. Word-by-Word Scroll Scrubbed Headings */
        root.querySelectorAll('[data-vrp-scrub]').forEach(function (heading) {
          var rawText = heading.textContent.trim();
          if (!rawText) return;
          heading.setAttribute('aria-label', rawText);
          var words = rawText.split(/\s+/);
          heading.innerHTML = words
            .map(function (w) {
              return '<span class="vrp-scrub-word" aria-hidden="true">' + w + '</span>';
            })
            .join(' ');

          var wordEls = heading.querySelectorAll('.vrp-scrub-word');
          gsap.to(wordEls, {
            opacity: 1,
            y: 0,
            stagger: 0.08,
            ease: 'none',
            scrollTrigger: {
              trigger: heading,
              start: 'top 88%',
              end: 'top 38%',
              scrub: 0.35,
            },
          });
        });

        /* B. Scorecard Reveal + Rating Distribution Fill */
        var scorecardEl = root.querySelector('[data-vrp-scorecard]');
        var distFillEl = root.querySelector('[data-vrp-dist-fill]');
        var metricFillEls = root.querySelectorAll('[data-vrp-metric-fill]');
        var reviewCards = root.querySelectorAll('[data-vrp-review-card]');
        var compactReviewCards = root.querySelectorAll('[data-vrp-review-compact]');

        if (scorecardEl) {
          gsap.fromTo(
            scorecardEl,
            { opacity: 0, y: 26 },
            {
              opacity: 1,
              y: 0,
              duration: 0.78,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: scorecardEl,
                start: 'top 86%',
                once: true,
              },
            }
          );
        }

        if (distFillEl) {
          gsap.fromTo(
            distFillEl,
            { scaleX: 0 },
            {
              scaleX: 1,
              duration: 0.92,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: scorecardEl || distFillEl,
                start: 'top 84%',
                once: true,
              },
            }
          );
        }

        if (metricFillEls.length) {
          gsap.fromTo(
            metricFillEls,
            { scaleX: 0 },
            {
              scaleX: 1,
              duration: 0.88,
              stagger: 0.1,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: scorecardEl || metricFillEls[0],
                start: 'top 84%',
                once: true,
              },
            }
          );
        }

        if (reviewCards.length) {
          gsap.fromTo(
            reviewCards,
            { opacity: 0, y: 28 },
            {
              opacity: 1,
              y: 0,
              duration: 0.76,
              stagger: 0.1,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: reviewCards[0],
                start: 'top 86%',
                once: true,
              },
            }
          );
        }

        if (compactReviewCards.length) {
          gsap.fromTo(
            compactReviewCards,
            { opacity: 0, y: 18 },
            {
              opacity: 1,
              y: 0,
              duration: 0.68,
              stagger: 0.08,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: compactReviewCards[0],
                start: 'top 90%',
                once: true,
              },
            }
          );
        }
      }, root);
    } else if (root) {
      /* No GSAP or reduced motion: ensure scrubbed words are fully visible */
      root.querySelectorAll('[data-vrp-scrub]').forEach(function (heading) {
        heading.querySelectorAll('.vrp-scrub-word').forEach(function (el) {
          el.style.opacity = '1';
          el.style.transform = 'none';
        });
      });
    }

    instances.set(root, { cleanups: cleanups, ctx: ctx });
  }

  function destroySection(root) {
    var inst = instances.get(root);
    if (!inst) return;
    if (inst.ctx) inst.ctx.revert();
    inst.cleanups.forEach(function (fn) {
      fn();
    });
    instances.delete(root);
  }

  function bootAll() {
    document.querySelectorAll('[data-vrp-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  /* Shopify Theme Editor Lifecycle Events */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-vrp-section]') || e.target;
    if (root && root.hasAttribute('data-vrp-section')) {
      initSection(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-vrp-section]') || e.target;
    if (root) destroySection(root);
  });
})();
